import React, { useCallback, useEffect, useState, FocusEvent } from 'react';
import { Form, Input, AutoComplete, FormInstance, FormItemProps, Alert } from 'antd';
import { css } from '@emotion/react';
import {
  GPPracticeFormatted,
  useGPPracticesSearch
} from '@components/Patients/Registration/utils/useGPPracticesSearch';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@vl-core/useRuntimeConfig';
import useIsNHS from '@hooks/useIsNHS';

const StyledAlert = styled(Alert)`
  background: transparent;
  border-color: transparent;
`;

interface DocmanODSFieldsProps {
  form: FormInstance;
  FormItemComponent: React.ComponentType<FormItemProps>;
  searchFieldName?: string;
  previewFieldName?: string;
  odsCodeFieldName?: string;
  searchExtra?: string;
  searchRules?: any[];
  initialSearchValue?: string;
  disabled?: boolean;
  hidden?: boolean;
  isConsentGiven?: boolean;
  onSelect?: (practice: any) => void;
  isGPFormVisible?: boolean;
  hasSavedGPWithDocmanNotActive?: boolean;
}

const gpAddressLookupRule = (isConsentGiven: boolean) => ({
  validator(_: any, value: string) {
    if (isConsentGiven && (!value || value.trim() === '')) {
      return Promise.reject(new Error('GP practice postcode is required when consent is given'));
    }
    return Promise.resolve();
  }
});

const gpAddressRequiredRule = (isGPFormVisible: boolean) => ({
  validator(_: any, value: string) {
    if (isGPFormVisible && (!value || value.trim() === '')) {
      return Promise.reject(new Error('GP practice is required'));
    }
    return Promise.resolve();
  }
});

const isValidPostcode = (postcode: string): boolean => {
  if (!postcode) return false;
  const postcodeRegex = /^[A-Z]{1,2}\d[\dA-Z]?\s?\d[A-Z]{2}$/i;
  return postcodeRegex.test(postcode);
};

const postcodeRule = () => ({
  validator(_: any, value: string) {
    if (value && !isValidPostcode(value)) {
      return Promise.reject(new Error('Please enter a valid postcode'));
    }
    return Promise.resolve();
  }
});

const DocmanActiveIndicator = ({
  practice,
  savedPracticeDocmanNotActive = false
}: {
  practice?: GPPracticeFormatted;
  savedPracticeDocmanNotActive?: boolean;
}) => {
  const { t } = useTranslation();
  if (!practice && !savedPracticeDocmanNotActive) return null;

  const shouldShowWarning = !practice?.docman_active || (!practice && savedPracticeDocmanNotActive);

  if (shouldShowWarning) {
    return (
      <span>
        <StyledAlert
          message={t('Warning')}
          description={
            <div>
              <p>{t('This practice does not yet accept electronic sharing')}</p>
              <p>
                {t(
                  'Please be aware that you are responsible for updating your health records with your GP to ensure the continuity of your care.'
                )}
              </p>
            </div>
          }
          type="warning"
          showIcon
        />
      </span>
    );
  }
  return null;
};

export const DocmanODSFields: React.FC<DocmanODSFieldsProps> = ({
  form,
  FormItemComponent = Form.Item,
  searchFieldName = 'gp_address_lookup',
  previewFieldName = 'gp_address_preview',
  odsCodeFieldName = 'gp_ods_code',
  searchExtra = 'Search for your GP by postcode',
  searchRules = [postcodeRule()],
  initialSearchValue,
  disabled = false,
  hidden = false,
  isConsentGiven = false,
  onSelect,
  isGPFormVisible = false,
  hasSavedGPWithDocmanNotActive = false
}) => {
  const [searchGPPostcode, setSearchGPPostcode] = useState<string>('');
  const { data: gpPracticesData } = useGPPracticesSearch(searchGPPostcode);
  const [selectedPractice, setSelectedPractice] = useState<GPPracticeFormatted>();
  const { DOCMAN } = useRuntimeConfig();
  const isNHS = useIsNHS();
  const { t } = useTranslation();

  useEffect(() => {
    if (hidden) {
      // this ensures fields are reset properly
      // and 'docman unavailable' warning is displayed correctly
      setSelectedPractice(undefined);
    }
  }, [hidden]);

  const handleSearch = useCallback((postcode: string) => {
    const upperPostcode = postcode.toUpperCase();
    if (isValidPostcode(upperPostcode)) {
      setSearchGPPostcode(upperPostcode);
    }
  }, []);

  const onSearchInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    handleSearch(value);
  };

  const handleSelect = useCallback(
    (index: number) => {
      const selectedPractice = gpPracticesData?.[index];
      setSelectedPractice(selectedPractice);
      if (selectedPractice) {
        form.setFieldsValue({
          [searchFieldName]: '',
          [previewFieldName]: selectedPractice.label,
          [odsCodeFieldName]: selectedPractice.ods_code,
          docman_active: selectedPractice.docman_active
        });

        onSelect?.(selectedPractice);
      }
    },
    [gpPracticesData, form, searchFieldName, previewFieldName, odsCodeFieldName, onSelect]
  );

  const inputStyles = css`
    width: 100%;
    .ant-select-selector {
      background-color: transparent !important;
    }
    input {
      text-transform: uppercase;
    }
  `;

  const PracticeAddress = styled(Input)`
    &[readonly] {
      color: var(--productGray) !important;
      background-color: rgba(0, 0, 0, 0.05);
    }
  `;

  return (
    <>
      <FormItemComponent label={t('We have the following GP on your record:')} labelAlign="left">
        <FormItemComponent
          name={previewFieldName}
          noStyle
          rules={[
            ...(isConsentGiven ? [gpAddressLookupRule(isConsentGiven)] : []),
            gpAddressRequiredRule(isGPFormVisible)
          ]}
        >
          <PracticeAddress readOnly required />
        </FormItemComponent>
        {!DOCMAN && !isNHS && (
          <p style={{ fontSize: '0.8em' }}>
            {t(
              'Please be aware that you are responsible for updating your health records with your GP to ensure the continuity of your care.'
            )}
          </p>
        )}
      </FormItemComponent>
      <div>
        <FormItemComponent
          label={
            <span style={{ display: 'inline-block' }}>
              {t('Please search by ')}
              <span style={{ fontWeight: 'bold' }}>&nbsp;postcode&nbsp;</span> {t('below')}{' '}
              <span style={{ fontWeight: 'bold' }}>&nbsp;only if you need to update your GP</span>
            </span>
          }
          labelAlign="left"
          name={searchFieldName}
          initialValue={initialSearchValue}
          rules={searchRules}
          hidden={hidden}
        >
          <AutoComplete
            options={
              gpPracticesData?.map((practice, i) => ({
                value: i.toString(),
                label: practice.label
              })) || []
            }
            onSearch={handleSearch}
            onSelect={handleSelect}
            onFocus={onSearchInputFocus}
            disabled={disabled}
            css={inputStyles}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
          />
        </FormItemComponent>
        {DOCMAN && (
          <DocmanActiveIndicator
            practice={selectedPractice}
            savedPracticeDocmanNotActive={hasSavedGPWithDocmanNotActive}
          />
        )}
      </div>
    </>
  );
};

export default DocmanODSFields;
